import type { AgentChat, AgentMessage } from '../types'

const DB_NAME = 'mp-agents-chats'
// v2: userId index. v3: messages store (chats no longer embed messages). v4: tenants.userId.
const DB_VERSION = 4
const CHATS_STORE = 'chats'
const MESSAGES_STORE = 'messages'

interface MessagesEntry {
  chatId: string
  messages: AgentMessage[]
}

type StoredChat = AgentChat & { messages?: AgentMessage[]; userId?: string }

class ChatStorage {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  private async init(): Promise<void> {
    if (this.db) return
    if (this.initPromise) return this.initPromise

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(new Error('Failed to open IndexedDB'))
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        const oldVersion = event.oldVersion

        // Chats store
        let chatsStore: IDBObjectStore
        if (db.objectStoreNames.contains(CHATS_STORE)) {
          chatsStore = (request.transaction as IDBTransaction)!.objectStore(CHATS_STORE)
        } else {
          chatsStore = db.createObjectStore(CHATS_STORE, { keyPath: 'id' })
        }
        if (!chatsStore.indexNames.contains('contextKey')) {
          chatsStore.createIndex('contextKey', 'contextKey', { unique: false })
        }
        if (!chatsStore.indexNames.contains('updatedAt')) {
          chatsStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }
        if (chatsStore.indexNames.contains('userId')) {
          chatsStore.deleteIndex('userId')
        }
        if (!chatsStore.indexNames.contains('tenants.userId')) {
          chatsStore.createIndex('tenants.userId', 'tenants.userId', { unique: false })
        }

        // Messages store (v3)
        if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
          const messagesStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'chatId' })
          messagesStore.createIndex('chatId', 'chatId', { unique: true })
        }
      }
    })

    return this.initPromise
  }

  private normalizeStoredChat(stored: StoredChat): AgentChat {
    const { messages: _messages, ...chat } = stored
    if (chat.tenants?.userId) {
      return chat as AgentChat
    }

    const legacyUserId = stored.userId ?? 'guest'
    return {
      ...(chat as AgentChat),
      tenants: {
        ...(chat.tenants ?? {}),
        userId: legacyUserId
      }
    }
  }

  async saveChat(chat: AgentChat): Promise<void> {
    await this.init()
    if (!this.db) throw new Error('IndexedDB not initialized')

    const { messages: _m, ...chatOnly } = chat as StoredChat
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([CHATS_STORE], 'readwrite')
      const store = transaction.objectStore(CHATS_STORE)
      const request = store.put(chatOnly)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to save chat'))
    })
  }

  async getChat(chatId: string): Promise<AgentChat | null> {
    await this.init()
    if (!this.db) throw new Error('IndexedDB not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([CHATS_STORE, MESSAGES_STORE], 'readwrite')
      const chatsStore = transaction.objectStore(CHATS_STORE)
      const request = chatsStore.get(chatId)

      request.onsuccess = async () => {
        const stored = request.result as StoredChat | undefined
        if (!stored) {
          resolve(null)
          return
        }
        // Migrate: if old chat has embedded messages, move to messages store and re-save chat
        if (stored.messages && Array.isArray(stored.messages)) {
          const messages = stored.messages
          const { messages: _m, ...chatOnly } = stored
          const messagesStore = transaction.objectStore(MESSAGES_STORE)
          messagesStore.put({ chatId, messages })
          chatsStore.put(chatOnly)
        }
        const chat = this.normalizeStoredChat(stored)
        chatsStore.put(chat)
        resolve(chat)
      }
      request.onerror = () => reject(new Error('Failed to get chat'))
    })
  }

  async listChats(filter?: { contextKey?: string; userId?: string; multiUser?: boolean }): Promise<AgentChat[]> {
    await this.init()
    if (!this.db) throw new Error('IndexedDB not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([CHATS_STORE, MESSAGES_STORE], 'readwrite')
      const store = transaction.objectStore(CHATS_STORE)
      const request = store.getAll()

      request.onsuccess = () => {
        let chats = (request.result as StoredChat[]) ?? []
        // Migrate: strip messages from any old chats and persist to messages store
        const messagesStore = transaction.objectStore(MESSAGES_STORE)
        chats = chats.map((stored) => {
          if (stored.messages && Array.isArray(stored.messages)) {
            messagesStore.put({ chatId: stored.id, messages: stored.messages })
            const chat = this.normalizeStoredChat(stored)
            store.put(chat)
            return chat
          }
          const chat = this.normalizeStoredChat(stored)
          store.put(chat)
          return chat
        })

        if (filter?.contextKey) {
          chats = chats.filter((chat) => chat.contextKey === filter.contextKey)
        }
        if (filter?.userId) {
          const targetUserId = filter.userId ?? 'guest'
          chats = chats.filter((chat) => (chat.tenants?.userId ?? 'guest') === targetUserId)
        }
        if (filter?.multiUser !== undefined) {
          chats = chats.filter((chat) => ((chat.participantIds?.length ?? 0) > 1) === filter.multiUser)
        }
        chats.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
        resolve(chats)
      }
      request.onerror = () => reject(new Error('Failed to list chats'))
    })
  }

  async deleteChat(chatId: string): Promise<void> {
    await this.init()
    if (!this.db) throw new Error('IndexedDB not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([CHATS_STORE, MESSAGES_STORE], 'readwrite')
      transaction.objectStore(CHATS_STORE).delete(chatId)
      transaction.objectStore(MESSAGES_STORE).delete(chatId)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(new Error('Failed to delete chat'))
    })
  }

  async clearAll(): Promise<void> {
    await this.init()
    if (!this.db) throw new Error('IndexedDB not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([CHATS_STORE, MESSAGES_STORE], 'readwrite')
      transaction.objectStore(CHATS_STORE).clear()
      transaction.objectStore(MESSAGES_STORE).clear()
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(new Error('Failed to clear'))
    })
  }

  // --- Messages (separate from chat metadata, consistent with ProxyTransport) ---

  async getMessages(chatId: string): Promise<AgentMessage[]> {
    await this.init()
    if (!this.db) return []

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([MESSAGES_STORE], 'readonly')
      const store = transaction.objectStore(MESSAGES_STORE)
      const request = store.get(chatId)

      request.onsuccess = () => {
        const entry = request.result as MessagesEntry | undefined
        resolve(entry?.messages ?? [])
      }
      request.onerror = () => reject(new Error('Failed to get messages'))
    })
  }

  async saveMessages(chatId: string, messages: AgentMessage[]): Promise<void> {
    await this.init()
    if (!this.db) throw new Error('IndexedDB not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([MESSAGES_STORE], 'readwrite')
      const store = transaction.objectStore(MESSAGES_STORE)
      const request = store.put({ chatId, messages })

      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to save messages'))
    })
  }

  async appendMessages(chatId: string, toAppend: AgentMessage[]): Promise<void> {
    const messages = await this.getMessages(chatId)
    messages.push(...toAppend)
    await this.saveMessages(chatId, messages)
  }
}

export const chatStorage = new ChatStorage()
